import { User } from 'src/app/models/User';
import { AuthService } from '../../../shared/services/auth.service';
import { Component, OnInit, OnDestroy } from '@angular/core';

import { MenuService } from '../menu/menu.service';

import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import {
  CLIENT,
  ADMINISTRATOR,
  AGENCY_CLIENT,
  PARTNER_CLIENT,
  UserService,
} from '../../../shared/services/user.service';
import { Notification } from 'src/app/models/Notification';
import { ToastService } from '../../../shared/components/toast/toast.service';
import {Subject, Subscription} from 'rxjs';
import { ListsService } from '../../../shared/services/lists.service';
import { BroadcastsService } from '../../../shared/services/broadcasts.service';
import { Call4UService } from 'src/app/shared/services/call4u.service';
import { IntegrationActivationStatus } from 'src/app/models/IntegrationActivated';
import {ConfirmationService} from "../../../shared/components/confirmation-modal/confirmation.service";
import {
  AccountVersion,
  AccountVersionService,
  INTERNATIONAL,
  NATIONAL,
} from '../../../shared/services/account-version.service';
import { WalletBalance } from 'src/app/models/Wallet';
import { AccountMemberSession, AccountMembersOverview } from 'src/app/models/AccountMember';
import { AccountMemberService } from 'src/app/shared/services/account-member.service';
import { formatNotificationDate } from './notification-date';

const NOTIFICATION_KEY = 'SMSFUNNEL::NOTIFICATIONS';

const FLAG_ICON = '/assets/icone-brasil.png';
const GLOBE_ICON = '/assets/icone-mundo.png';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {

  user: User;
  version: AccountVersion = NATIONAL;
  timer = null;
  updateNotificationsTimer = null;
  timerNotifications = null;

  isMenuVisible: boolean;
  isSmallDevice: boolean;
  showDropdown: boolean;
  showNotifications: boolean;

  usedCall4U: boolean;
  togglePopUp: boolean = false;

  notifications: Notification[] = [];

  /**
   * Container em vez de array porque nem toda assinatura daqui é do ciclo de
   * vida: `listAlerts()` roda a cada 30 segundos e `markAsRead()` a cada clique,
   * e o array só crescia — ninguém tirava nada dele enquanto o header existisse.
   * O `Subscription` do RxJS solta sozinho o filho que já completou, que é o
   * caso de toda requisição HTTP, e o `unsubscribe()` do destroy continua
   * cancelando o que estiver em voo.
   */
  subscriptions = new Subscription();

  showWalletModal = new Subject<string>();

  /**
   * Último saldo que o modal leu. O header não consulta a carteira: ele só
   * recebe o resultado da abertura, e é isso que mantém o badge de saldo
   * negativo sem custar uma requisição por navegação.
   */
  walletBalance: WalletBalance = null;

  showMembersModal = new Subject<void>();

  /**
   * Multiusuários (CU-17tm466h1cm). `null` enquanto não houver resposta, quando
   * o recurso não vale para esta conta (flag desligada ou perfil que não tem
   * membros) e quando quem logou é membro, que não gerencia a equipe e nem
   * consulta. Para o dono sem o recurso, o item do menu e o card de identidade
   * não aparecem e o header fica exatamente como era.
   */
  membersOverview: AccountMembersOverview = null;

  /**
   * De qual conta é o `membersOverview` (`null` = nenhuma: deslogado ou
   * simulando). O `userChanged` sai a cada releitura do cadastro; só a troca
   * de conta — começar ou encerrar uma simulação — pede outra consulta.
   */
  private membersAccountId: string = null;

  /** A consulta do item em voo: uma por vez, a anterior cancelada ao trocar. */
  private membersOverviewSubscription: Subscription;

  /**
   * Quem está logado quando é membro; o `user` continua sendo a conta. A
   * saudação do topo é da conta (com o selo "Membro"); `displayName` e
   * `displayEmail` alimentam o card de identidade do dropdown.
   */
  get member(): AccountMemberSession | null {
    return this.authService.currentMember ?? null;
  }

  get isMember(): boolean {
    return this.member !== null;
  }

  get displayName(): string {
    return this.member?.name ?? this.user.name;
  }

  get displayEmail(): string {
    return this.member?.email ?? this.user.email;
  }

  get showsMultiuser(): boolean {
    return this.membersOverview !== null;
  }

  /** Contagem do item do menu: membros da conta, sem o dono. */
  get membersCountLabel(): string {
    const count = this.membersOverview?.used ?? 0;
    return count === 1 ? '1 membro' : `${count} membros`;
  }

  get hasWalletOverdraft(): boolean {
    return this.walletBalance?.overdraft === true;
  }

  get walletTooltip(): string {
    if (this.hasWalletOverdraft) {
      return 'Sua carteira está negativa. Clique para ver.';
    }

    return 'Minha carteira: saldo e extrato';
  }

  get selectedNotifications() {
    return this.notifications.filter((n) => n.checked);
  }

  /**
   * Mais novo primeiro (mockup `state-bell`, 17tm466frtx). `.reverse()` direto
   * no template mutava o array a cada ciclo de detecção — além de inverter
   * pela ORDEM de chegada, não pela data, então um reload que mudasse a ordem
   * do backend bagunçava o sino sem ninguém tocar em nada. Cópia nova,
   * ordenada pela data de verdade.
   */
  get sortedNotifications(): Notification[] {
    return [...this.notifications].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }

  /** "hoje, 09:00" / "ontem, 16:40" / "19/10, 11:02" — 2ª linha de cada aviso do sino. */
  notificationDateLabel(date: Date): string {
    return formatNotificationDate(date);
  }

  get isClient(): boolean {
    return this.user.profile_id === CLIENT;
  }

  get isAffiliate(): boolean {
    return this.user.affiliation ? true : false;
  }

  get isAdministrator(): boolean {
    return this.user.profile_id === ADMINISTRATOR;
  }

  get isInternational(): boolean {
    return this.version === INTERNATIONAL;
  }

  /**
   * O switch de versão só existe para quem tem acesso ao internacional
   * (`intl_enabled`). A condição antiga era `user.global`, que significa conta
   * com credencial Twilio, e não acesso ao internacional. Ausência de
   * `intl_enabled` mantém o switch escondido.
   *
   * Não some `globalActivated` aqui para "não deixar ninguém preso"
   * (CU-86akktm54): sem `intl_enabled` o realm já é nacional por invariante
   * (`AuthService.enforceRealmAccess`), então não existe internacional de onde
   * sair. Um escape nesta linha reabriria o estado inconsistente em vez de
   * fechá-lo, e daria uma segunda definição de quem pode ser internacional.
   */
  get canSwitchVersion(): boolean {
    return this.user?.intl_enabled === true;
  }

  get versionName(): string {
    return this.isInternational ? 'Internacional' : 'Brasil';
  }

  /**
   * Rótulo da pílula de escopo (CU-86akn0zp6).
   *
   * Mais longo que o `versionName` do chip de tela pequena de propósito: no
   * desktop a pílula é o único lugar que diz de qual operação é o dinheiro da
   * tela, e "Brasil" sozinho não diz que existe a outra.
   */
  get scopeLabel(): string {
    return this.isInternational ? 'INTERNACIONAL' : 'BRASIL (NACIONAL)';
  }

  /** Mesmo texto no desktop e no dropdown — a ação é a mesma. */
  get scopeTooltip(): string {
    return 'Clique para alternar o tipo de conta';
  }

  get versionIcon(): string {
    return this.isInternational ? GLOBE_ICON : FLAG_ICON;
  }

  get switchVersionLabel(): string {
    return this.isInternational ? 'Ir para Brasil' : 'Ir para Internacional';
  }

  get switchVersionIcon(): string {
    return this.isInternational ? FLAG_ICON : GLOBE_ICON;
  }

  constructor(
    private menuService: MenuService,
    private authService: AuthService,
    private router: Router,
    private toast: ToastService,
    private userService: UserService,
    private listsService: ListsService,
    private broadcastsService: BroadcastsService,
    private call4UService: Call4UService,
    private confirmationService: ConfirmationService,
    private accountVersionService: AccountVersionService,
    private accountMemberService: AccountMemberService
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.menuService
        .getMenuVisibility()
        .subscribe((isVisible) => (this.isMenuVisible = isVisible)),
    );

    this.subscriptions.add(
      this.menuService
        .getIsSmallDevice()
        .subscribe((isSmallDevice) => (this.isSmallDevice = isSmallDevice))
    );

    this.user = this.authService.getUser();

    this.subscriptions.add(
      this.accountVersionService
        .getVersion()
        .subscribe((version) => (this.version = version))
    );

    this.subscriptions.add(
      this.authService.userChanged.subscribe((user) => {
        this.user = user;
        this.listAlerts();
        this.loadMembersOverview();
      })
    );

    this.subscriptions.add(
      this.listsService.importFinished.subscribe(() => this.listAlerts())
    );

    this.subscriptions.add(
      this.broadcastsService.importFinished.subscribe(() => this.listAlerts())
    );

    this.loadNotifications();
    this.listAlerts();
    this.loadMembersOverview();

    this.call4UService.isActivated().subscribe(
      (response: IntegrationActivationStatus) => {
        this.usedCall4U = response.status;
      }
    );

    if (this.updateNotificationsTimer) {
      clearInterval(this.updateNotificationsTimer);
    }

    this.updateNotificationsTimer = setInterval(() => {
      this.listAlerts();
    }, 30 * 1000);

  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.membersOverviewSubscription?.unsubscribe();
    if (this.updateNotificationsTimer) {
      clearInterval(this.updateNotificationsTimer);
    }
  }

  listAlerts() {
    if (this.authService.isLogged()) {
      this.subscriptions.add(
        this.userService
          .listAlerts()
          .subscribe((notifications) =>
            this.verifyNewNotifications(notifications)
          )
      );
    }
  }

  verifyNewNotifications(notifications: Notification[]) {
    const savedNotifications = localStorage.getItem(NOTIFICATION_KEY);
    let updateUser:boolean = false;

    notifications.forEach(notification => {
      if(notification.message === "A migração de dados da lista solicitada terminou." || notification.message === "Não existe uma lista correspondente à migração da lista solicitada.") {
        updateUser = true;
      }
    })

    if (updateUser) {
      const userLogged: User = { ...this.authService.getUser() };
      this.subscriptions.add(this.userService.findById(userLogged.id).subscribe((userDatabase: User) => {
        if (userDatabase.migration_in_progress !== userLogged.migration_in_progress) {
          // `setUser` já emite `userChanged`. O `emit` que havia aqui dobrava
          // toda a reatividade a cada refresh: menu recarregado duas vezes,
          // `listAlerts()` refeito duas vezes, telas de carteira consultando
          // dinheiro duas vezes por evento.
          //
          // Nem o realm nem o DESTINO são assunto desta linha. `userDatabase`
          // vem cru do backend, que não devolve `globalActivated`, e durante
          // uma simulação descreve o cliente simulado, não o admin logado —
          // quem decide as duas coisas é o `AuthService` (ver `setUser`).
          // Repetir aqui o desvio `if (getSimulatedUser())` dos outros
          // chamadores fecharia UM caminho e deixaria o próximo com o mesmo
          // buraco, que é como este bug chegou até aqui.
          this.authService.setUser(userDatabase);
        }
      }));
    }

    if (savedNotifications) {
      const newNotifications = notifications.filter((n) => {
        const exist = this.notifications.find(
          (notification) => notification.id === n.id
        );
        return exist ? false : true;
      });

      // newNotifications.forEach(n => this.toast.success(n.message));
    }

    this.saveNotifications(notifications);
  }

  markAllRead() {
    this.notifications.forEach((n) => this.markAsRead(n));
  }

  saveNotifications(notifications: Notification[]) {
    this.notifications = notifications;
    localStorage.setItem(NOTIFICATION_KEY, JSON.stringify(notifications));
  }

  loadNotifications() {
    const savedNotifications = localStorage.getItem(NOTIFICATION_KEY);
    if (savedNotifications) {
      const notifications: Notification[] = JSON.parse(savedNotifications);
      this.notifications = notifications;
    }
  }

  toggleDropdown(): void {
    this.showDropdown = !this.showDropdown;
  }

  /**
   * Hambúrguer (CU-86akkvmxc): o único gatilho da barra lateral. No desktop
   * alterna barra expandida e trilho de ícones, e a escolha fica salva; no
   * celular abre e fecha a gaveta.
   */
  toggleMenu(): void {
    this.menuService.toggle();
  }

  get menuToggleLabel(): string {
    return this.isMenuVisible ? 'Recolher menu' : 'Abrir menu';
  }

  /** Único caminho de troca de versão — o sidebar não tem mais o botão. */
  toggleAccountVersion(): void {
    this.showDropdown = false;
    this.accountVersionService.toggle();
  }

  toggleNotifications(over: boolean = false) {
    this.stopCloseNotifications();
    if (over) {
      this.showNotifications = true;
    } else {
      this.timerNotifications = setTimeout(
        () => (this.showNotifications = false),
        300
      );
    }
  }

  stopCloseNotifications() {
    if (this.timerNotifications) {
      clearTimeout(this.timerNotifications);
    }
  }

  markAsRead(notification: Notification) {
    this.subscriptions.add(
      this.userService.updateAlert(notification.id).subscribe({
        next: () => {
          this.notifications = this.notifications.filter(
            (n) => n.id !== notification.id
          );
          this.toggleNotifications(true);
          if (this.notifications.length === 0) {
            this.toast.success('Todas as notificações foram lidas!');
          }
          setTimeout(() => {
            this.toggleNotifications(false);
          }, 300);
        },
      })
    );
  }
  startCloseTime(): void {
    this.stopCloseTime();
    this.timer = setTimeout(() => {
      if (this.showDropdown) {
        this.showDropdown = false;
      }
    }, 300);
  }

  stopCloseTime(): void {
    if (this.timer) {
      clearTimeout(this.timer);
    }
  }

  closeCall4U(activated: boolean): void {
    if (activated) {
      const call4uHeader = document.body.querySelector('#call4u-header');
      call4uHeader.remove();
    }
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  backToLoggedInUser(): void {
    this.authService.setSimulatedUser(null);
    this.router.navigate(['/users']);
  }

  openPopup(): void {
    this.togglePopUp = !this.togglePopUp;
  }

  openWalletModal(): void {
    this.showWalletModal.next('');
  }

  openMembersModal(): void {
    this.showDropdown = false;
    this.showMembersModal.next();
  }

  /**
   * Lista que o modal leu. Ela é mais nova que uma consulta do header ainda em
   * voo, então essa consulta é cancelada: a resposta atrasada não pode
   * sobrescrever a contagem certa.
   */
  onMembersOverviewLoaded(overview: AccountMembersOverview): void {
    // Releitura do modal que respondeu já simulando outra conta: não é dela.
    if (!this.membersAccountId) {
      return;
    }

    this.membersOverviewSubscription?.unsubscribe();
    this.applyMembersOverview(overview);
  }

  /** Só uma resposta com a lista conta como "recurso ligado"; corpo vazio, não. */
  private applyMembersOverview(overview: AccountMembersOverview): void {
    this.membersOverview = Array.isArray(overview?.members) ? overview : null;
  }

  /** O modal fechou sem entregar a lista depois de uma mudança: relê aqui. */
  onMembersOverviewStale(): void {
    this.loadMembersOverview(true);
  }

  /**
   * Uma consulta por conta exibida, para saber se o item "Multiusuários"
   * aparece e com qual contagem. O 403 `multiuser_unavailable` (flag desligada,
   * perfil sem membros) não é erro: só deixa o header como sempre foi.
   *
   * O membro não gerencia a equipe: não consulta e o item não aparece.
   * Simulando um cliente, o Admin (ou a Agência, sobre a filha) também não: o
   * item some. O header não é recriado ao simular nem ao
   * encerrar a simulação, por isso isto roda também no `userChanged`.
   *
   * `refresh` relê a mesma conta (a contagem mudou pelo modal) mantendo a
   * contagem atual na tela até a resposta, sem piscar o item.
   */
  private loadMembersOverview(refresh = false): void {
    // `isSimulating` lê o armazenamento: não depende de o objeto emitido no
    // `userChanged` trazer a flag `simulated`.
    const simulating = this.user?.simulated || this.authService.isSimulating;
    // A equipe é gerenciada só pelo dono: o membro não vê o item nem consulta.
    const accountId = this.authService.isLogged() && this.user && !simulating && !this.isMember ? this.user.id : null;

    const sameAccount = accountId === this.membersAccountId;

    if (sameAccount && !(refresh && accountId)) {
      return;
    }

    this.membersAccountId = accountId;
    this.membersOverviewSubscription?.unsubscribe();

    if (!sameAccount) {
      this.membersOverview = null;
    }

    if (!accountId) {
      return;
    }

    this.membersOverviewSubscription = this.accountMemberService.overview().subscribe({
      next: (overview) => this.applyMembersOverview(overview),
      // Na releitura, uma falha passageira não apaga o item que estava certo.
      error: (error: HttpErrorResponse) => {
        if (sameAccount) {
          return;
        }

        this.membersOverview = null;

        // Só o 403 é resposta definitiva ("recurso indisponível"). Numa falha
        // passageira a conta não fica marcada como consultada: a próxima
        // emissão do `userChanged` tenta de novo, em vez de o item sumir de vez.
        if (error?.status !== 403) {
          this.membersAccountId = null;
        }
      },
    });
  }

  onWalletBalanceLoaded(balance: WalletBalance | null): void {
    this.walletBalance = balance;
  }
}
